import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill, InServerBadge, RelocateHint } from "@/components/network/StatusPill";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { KodeHistory } from "@/components/network/KodeHistory";
import { ServerInfo } from "@/components/network/ServerInfo";
import { ArchiveInfraButton } from "@/components/network/ArchiveInfraButton";
import { OtbCoreSummary } from "@/components/network/OtbCoreSummary";
import { CABLE_JENIS_LABEL, fmtLen } from "@/lib/network";

const end = (k, e) => `${(k || "odp").toUpperCase()} ${e?.kode || "?"}`;

function CableRow({ c, otbId, onSelect, tid }) {
  const out = c.from_kind === "otb" && c.from_id === otbId;
  const [k, e] = out ? [c.to_kind, c.to] : [c.from_kind, c.from];
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded bg-[#F8F9FA] p-1.5 text-xs" data-testid={tid}>
      <button className="font-mono-tab font-semibold text-[#0055FF] hover:underline" onClick={() => onSelect?.({ kind: "cable", id: c.id })}>{c.kode}</button>
      <span>{out ? "→" : "←"} {e ? <button className="font-semibold hover:underline" onClick={() => onSelect?.({ kind: k || "odp", id: e.id })}>{end(k, e)}</button> : end(k, e)}</span>
      <span className="text-[#6B7280]">{CABLE_JENIS_LABEL[c.jenis] || c.jenis} · {fmtLen(c.panjang_m)}</span><StatusPill status={c.status} />
    </li>
  );
}

// Detail OTB: info, kabel terhubung langsung, dan penelusuran kabel bertingkat (BFS, tanpa duplikasi).
export function OtbDetail({ otbId, me, wilayahName, onSelect, onDeleted, onArchived, reloadKey }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/network/otb/${otbId}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat detail OTB"));
  }, [otbId, reloadKey]);
  if (!d) return <p className="text-xs" data-testid="otb-detail-loading">Memuat…</p>;
  return (
    <div className="space-y-3 text-sm" data-testid="otb-detail">
      <div className="flex items-start justify-between gap-2">
        <div><div className="font-display text-xl font-bold" data-testid="otb-detail-kode">{d.kode}</div>
          <div className="text-xs text-[#6B7280]">{d.nama} · {wilayahName(d.wilayah_id)}</div></div>
        <div className="flex flex-wrap justify-end gap-1"><ArchiveInfraButton kind="otb" item={d} me={me} onDone={onArchived || onDeleted} />
          <DeleteInfraButton kind="otb" item={d} me={me} onDeleted={onDeleted} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {d.archived && <span className="rounded bg-[#E5E7EB] px-1.5 py-0.5 font-semibold text-[#374151]" data-testid="otb-detail-archived">ARSIP · {d.archived_by} · {(d.archived_at || "").slice(0, 10)}</span>}
        <StatusPill status={d.status} /><span className="rounded bg-[#ECFEFF] px-1.5 py-0.5 font-semibold text-[#0E7490]" data-testid="otb-detail-core">{d.jumlah_core} core</span>
        {d.in_server ? <InServerBadge s={d.in_server} testid="otb-detail-inserver" />
          : !d.has_coords && <span className="rounded border border-dashed border-[#F59E0B] px-1.5 py-0.5 text-[#B45309]" data-testid="otb-detail-nocoord">Lokasi peta belum ditentukan</span>}
        {!d.in_server && <ArahkanButton lat={d.latitude} lng={d.longitude} testid="otb-nav" />}
      </div>
      {d.alamat && <p className="text-xs">{d.alamat}</p>}
      <OtbCoreSummary core={d.core} />
      <section><b className="text-xs uppercase text-[#6B7280]">Kabel Terhubung ({d.cables.length})</b>
        {d.cables.length ? <ul className="mt-1 space-y-1">{d.cables.map((c) => <CableRow key={c.id} c={c} otbId={d.id} onSelect={onSelect} tid={`otb-cable-${c.kode}`} />)}</ul>
          : <p className="text-xs text-[#6B7280]" data-testid="otb-no-cable">Belum ada kabel terhubung.</p>}</section>
      {d.trace.length > 0 && <details className="text-xs" data-testid="otb-trace"><summary className="cursor-pointer font-semibold text-[#6B7280]">Jalur Kabel ({d.trace.length} kabel)</summary>
        <ul className="mt-1 space-y-1">{d.trace.map((t) => (
          <li key={t.cable.id} className="rounded bg-[#F8F9FA] p-1.5" style={{ marginLeft: (t.depth - 1) * 12 }} data-testid={`otb-trace-${t.cable.kode}`}>
            <span className="text-[#6B7280]">L{t.depth}</span> {end(t.cable.from_kind, t.cable.from)} → {end(t.cable.to_kind, t.cable.to)}
            <span className="text-[#6B7280]"> · {t.cable.kode} · {fmtLen(t.cable.panjang_m)}</span></li>))}</ul></details>}
      {d.catatan && <p className="text-xs text-[#6B7280]">Catatan: {d.catatan}</p>}
      <p className="text-[11px] text-[#6B7280]">Diubah {d.updated_by} · {(d.updated_at || "").slice(0, 10)}</p>
      <RelocateHint show={!d.in_server && !d.has_coords && (d.cables || []).length > 0} testid="otb-relocate-hint" />
      <ServerInfo kind="otb" id={d.id} onSelect={onSelect} />
      <KodeHistory kind="otb" id={d.id} kode={d.kode} />
    </div>
  );
}
